import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { brl, dateFmt } from "@/lib/format";
import {
  FileText,
  Users,
  Layers,
  CheckCircle2,
  Clock,
  DollarSign,
  ArrowRight,
} from "lucide-react";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="card-tactical rounded-lg p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-400">{label}</div>
        <div className={`mt-2 display text-3xl font-bold ${accent || "text-slate-100"}`}>
          {value}
        </div>
      </div>
      <div className="w-9 h-9 rounded-md bg-slate-800/70 grid place-content-center text-orange-300">
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-view" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
            painel · overview
          </div>
          <h1 className="display text-3xl sm:text-4xl font-bold mt-1">Central de Comando</h1>
        </div>
        <Link
          to="/orcamentos/novo"
          className="pill-accent rounded-md px-4 py-2 text-sm font-semibold hover:opacity-95"
          data-testid="dashboard-new-quote-cta"
        >
          + Novo Orçamento
        </Link>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Orçamentos" value={stats?.total_quotes ?? "—"} icon={FileText} />
        <Stat
          label="Aprovados"
          value={stats?.approved_quotes ?? "—"}
          icon={CheckCircle2}
          accent="text-emerald-400"
        />
        <Stat
          label="Pendentes"
          value={stats?.pending_quotes ?? "—"}
          icon={Clock}
          accent="text-amber-400"
        />
        <Stat
          label="Valor total"
          value={stats ? brl(stats.total_value) : "—"}
          icon={DollarSign}
          accent="text-orange-400"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-tactical rounded-lg p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="display text-lg font-semibold">Orçamentos recentes</h2>
            <Link to="/orcamentos" className="text-xs text-orange-300 hover:underline">
              ver todos <ArrowRight className="inline w-3 h-3" />
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2">Nº</th>
                  <th className="text-left py-2">Data</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody data-testid="recent-quotes-tbody">
                {(stats?.recent_quotes ?? []).map((q) => (
                  <tr key={q.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="py-2.5">
                      <Link
                        to={`/orcamentos/${q.id}`}
                        className="mono text-orange-300 hover:underline"
                      >
                        {q.number}
                      </Link>
                    </td>
                    <td className="py-2.5 text-slate-300">{dateFmt(q.created_at)}</td>
                    <td className="py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                        {q.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-right mono text-slate-100">
                      {brl(q.totals?.total)}
                    </td>
                  </tr>
                ))}
                {(!stats?.recent_quotes || stats.recent_quotes.length === 0) && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">
                      Nenhum orçamento ainda — crie o primeiro!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-3">
          <div className="card-tactical rounded-lg p-5">
            <div className="text-[11px] mono uppercase tracking-widest text-slate-400">
              cadastros
            </div>
            <div className="mt-4 space-y-3">
              <Link
                to="/clientes"
                className="flex items-center justify-between p-3 rounded-md bg-slate-900/60 hover:bg-slate-800/70 transition"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-orange-300" />
                  <span className="text-sm">Clientes</span>
                </div>
                <span className="mono text-slate-300">{stats?.total_clients ?? "—"}</span>
              </Link>
              <Link
                to="/chapas"
                className="flex items-center justify-between p-3 rounded-md bg-slate-900/60 hover:bg-slate-800/70 transition"
              >
                <div className="flex items-center gap-3">
                  <Layers className="w-4 h-4 text-orange-300" />
                  <span className="text-sm">Chapas</span>
                </div>
                <span className="mono text-slate-300">{stats?.total_sheets ?? "—"}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
